import React from "react";
import { BrowserRouter, Routes as RouterRoutes, Route, Navigate } from "react-router-dom";
import ProtectedRoute from "components/ProtectedRoute";
import ErrorBoundary from "components/ErrorBoundary";
import ScrollToTop from "components/ScrollToTop";
import NotFound from "pages/NotFound";
import ArtistDetailEditorView from './pages/artist-detail-editor-view';
import ArtistDetailAdminView from './pages/artist-detail-admin-view';
import ArtistContentManagement from './pages/artist-content-management';
import AdminDashboard from './pages/admin-dashboard';
import LoginPage from './pages/login';
import EditorDashboard from './pages/editor-dashboard';
import MobileAppPreview from './pages/mobile-app-preview';
import HelpSupport from './pages/help-support';
import ArtistMetricsDashboard from './pages/artist-metrics-dashboard';
import ArtistNotificationsManager from './pages/artist-notifications-manager';
import UserActivityLogs from './pages/user-activity-logs';
import AdvancedAnalyticsDashboard from './pages/advanced-analytics-dashboard';
import ApiKeyManagement from 'pages/api-key-management';
import { useAuth } from './contexts/AuthContext';

// Component to handle authenticated user redirection
const AuthenticatedRedirect = () => {
  const { user, userProfile, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Redirect based on user role
  const roleRoutes = {
    admin: '/admin-dashboard',
    editor: '/editor-dashboard',
    artist: '/artist-content-management'
  };

  const userRole = userProfile?.role;
  const redirectPath = roleRoutes?.[userRole] || '/admin-dashboard';
  
  return <Navigate to={redirectPath} replace />;
};

const Routes = () => {
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <ScrollToTop />
        <RouterRoutes>
          <Route path="/" element={<AuthenticatedRedirect />} />
          
          <Route path="/login" element={<LoginPage />} />
          
          <Route 
            path="/admin-dashboard" 
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminDashboard />
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/editor-dashboard" 
            element={
              <ProtectedRoute allowedRoles={['editor', 'admin']}>
                <EditorDashboard />
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/artist-content-management" 
            element={
              <ProtectedRoute allowedRoles={['artist', 'admin']}>
                <ArtistContentManagement />
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/artist-detail-editor-view" 
            element={
              <ProtectedRoute allowedRoles={['editor', 'admin']}>
                <ArtistDetailEditorView />
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/artist-detail-admin-view" 
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <ArtistDetailAdminView />
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/mobile-app-preview" 
            element={
              <ProtectedRoute allowedRoles={['artist', 'editor', 'admin']}>
                <MobileAppPreview />
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/help-support" 
            element={
              <ProtectedRoute allowedRoles={['artist', 'editor', 'admin']}>
                <HelpSupport />
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/artist-metrics-dashboard" 
            element={
              <ProtectedRoute allowedRoles={['artist', 'admin']}>
                <ArtistMetricsDashboard />
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/artist-notifications-manager" 
            element={
              <ProtectedRoute allowedRoles={['artist', 'admin']}>
                <ArtistNotificationsManager />
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/user-activity-logs" 
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <UserActivityLogs />
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/advanced-analytics-dashboard" 
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdvancedAnalyticsDashboard />
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/api-key-management" 
            element={
              <ProtectedRoute allowedRoles={['admin', 'artist']}>
                <ApiKeyManagement />
              </ProtectedRoute>
            } 
          />
          
          <Route path="*" element={<NotFound />} />
        </RouterRoutes>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default Routes;